import axios from 'axios';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const useAuthStore = create(
  persist(
    (set) => ({
      accessToken: null,
      setAccessToken: (token) => set({ accessToken: token }),
      clearAccessToken: () => set({ accessToken: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ accessToken: state.accessToken })
    }
  )
);

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/',
  headers: { 'Content-Type': 'application/json' }
});

// Axios response interceptor: accessToken güncelle
api.interceptors.response.use(
  (response) => {
    if (response.data?.data?.accessToken) {
      useAuthStore.getState().setAccessToken(response.data.data.accessToken);
    }
    return response;
  },
  (error) => Promise.reject(error)
);

// Ortak yetkisiz response oluşturucu
function unauthorizedResponse() {
  return new Response(
    JSON.stringify({
      success: false,
      message: 'Session expired. Please login again.',
      data: 'Access Denied',
      httpStatus: 'UNAUTHORIZED'
    }),
    { status: 401, headers: { 'Content-Type': 'application/json' } }
  );
}

// Gelişmiş fetch helper
export async function fetchWithToken(url, options = {}) {
  const { accessToken, setAccessToken, clearAccessToken } = useAuthStore.getState();

  async function tryRefresh() {
    try {
      const res = await fetch('/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data?.accessToken) {
          setAccessToken(data.data.accessToken);
          return data.data.accessToken;
        }
      }
    } catch (e) {
      // Refresh sırasında hata olursa devam et
    }

    // Refresh başarısızsa temizle ve yönlendir
    clearAccessToken();
    localStorage.clear();
    sessionStorage.clear();
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    return null;
  }

  let token = accessToken;

  // Token yoksa önce refresh dene
  if (!token && !url.includes('/v1/auth/refresh')) {
    token = await tryRefresh();
    if (!token) return unauthorizedResponse();
  }

  // İlk isteği gönder
  let response = await fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(url.includes('/v1/auth/refresh') ? { credentials: 'include' } : {}),
  });

  // Eğer 401 aldıysak token süresi dolmuş olabilir → refresh dene
  if (response.status === 401 && !url.includes('/v1/auth/refresh')) {
    token = await tryRefresh();
    if (!token) return unauthorizedResponse();

    // Yeni token ile isteği tekrar gönder
    response = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
  }

  return response;
}

export default api;
